import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  name: string;
  email: string;
  terminals: string[];
}

export interface UpdateUserTerminalsCmd {
  userId: string;
  terminals: string[];
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'user';

const fetchUsers = async (): Promise<User[]> => {
  return apiRequest<void, User[]>(HttpMethod.GET, API_ENDPOINT);
};

const fetchUser = async (id: string): Promise<User> => {
  return apiRequest<void, User>(HttpMethod.GET, `${API_ENDPOINT}/${id}`);
};

const updateUsersFromAD = async (): Promise<void> => {
  return apiRequest<void, void>(HttpMethod.PUT, `${API_ENDPOINT}/updateUsersFromAd`);
};

const updateUserTerminals = async (cmd: UpdateUserTerminalsCmd): Promise<User> => {
  return apiRequest<UpdateUserTerminalsCmd, User>(
    HttpMethod.POST,
    `${API_ENDPOINT}/${cmd.userId}/terminal/`,
    cmd
  );
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all users
 */
export const useUsersQuery = () => {
  return useQuery({
    queryKey: queryKeys.users.all,
    queryFn: fetchUsers,
  });
};

/**
 * Fetch a single user by ID
 */
export const useUserQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: [...queryKeys.users.all, 'detail', id],
    queryFn: () => fetchUser(id!),
    enabled: !!id,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Update users from Active Directory
 */
export const useUpdateUsersFromADMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUsersFromAD,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toastService.success({ title: 'users.update_from_ad_success' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update user terminal assignments
 */
export const useUpdateUserTerminalsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserTerminals,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};
